import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { User } from '../types';
import { api } from '../services/api';
import { Button, Field, Input } from './ui';

interface LoginProps {
  onLogin: (user: User, token: string) => void;
  notice?: string | null;
}

const Login: React.FC<LoginProps> = ({ onLogin, notice }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);
    try {
      const { user, token } = await api.login(username, password);
      onLogin(user, token);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] bg-sand-50 flex items-center justify-center p-6">
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(800px 400px at 80% 0%, rgba(91,138,114,0.10), transparent 70%),' +
            'radial-gradient(600px 400px at 0% 100%, rgba(194,105,59,0.08), transparent 70%)',
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-2xl bg-sage-600 text-white inline-flex items-center justify-center font-display text-xl">
              O
            </span>
            <span className="text-sand-900 font-display text-2xl font-medium">
              Otto's Barn
            </span>
          </div>
          <h1 className="text-2xl font-semibold text-sand-900 tracking-tight">
            Welcome back
          </h1>
          <p className="text-sand-500 mt-1">
            Sign in to manage bookings, guests and your calendar.
          </p>
        </div>

        {notice && (
          <div className="mb-4 rounded-xl bg-sage-50 border border-sage-200 px-3.5 py-2 flex items-start gap-2 text-sm">
            <Info className="h-4 w-4 text-sage-700 mt-0.5 shrink-0" />
            <span className="text-sage-800">{notice}</span>
          </div>
        )}

        <div className="bg-white border border-sand-200 rounded-2xl shadow-soft p-7">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Username" htmlFor="username" required>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="andrew"
                required
                disabled={isLoggingIn}
              />
            </Field>

            <Field label="Password" htmlFor="password" required>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoggingIn}
              />
            </Field>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isLoggingIn}
              className="mt-2"
            >
              {isLoggingIn ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="text-center text-xs text-sand-500 mt-6">
            JWT-protected session · &copy; {new Date().getFullYear()} Otto's Barn
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
